/* eslint-disable no-param-reassign */
import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { FINANCIALS_REFERENCE_TITLE, FINANCIALS_REFERENCE_TYPE } from 'common/constants/documents';
import { FINANCIALS_PAGE_KEY } from 'common/constants/notes';
import { CompanyInfo } from 'common/types/company';
import { ExcelExportContextValues } from 'common/types/store';
import { GridSkeleton } from 'components/Grid';
import ExcelExportContext from 'context/ExcelExportContext';
import { FinancialsProvider } from 'context/FinancialsContext';
import LayoutContext from 'context/LayoutContext';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { CaptableService } from 'services';
import { useCompanyMeasurementDateFilter, useDocuments } from 'services/hooks';
import { useGetPrimaryCaptableByCompanyDate } from 'services/hooks/captable';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { Root } from './Financials.styles';
import FinancialsTables from './income-statement';
import useLoadFinancials from './utilities/useLoadFinancials';
import { WidgetDrawerApplicationContext } from '../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { DocumentFootnotesProvider } from '../../context';
import { ActiveCellProvider } from '../../context/ActiveCellContext';

const Financial = () => {
  /* Hooks */
  const { viewOnlyUser, isExportMode } = useContext(LayoutContext);
  const { filesToSave, setFilesToSave } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    saveSpreadsheetNotes,
    hasChanged: spreadsheetNoteHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const { selectedMeasurementDate, MDDialogue: MDDialog } = useCompanyMeasurementDateFilter();
  const {
    periods,
    changePeriodDate,
    incomeStatement,
    setIncomeStatement,
    balanceSheets,
    setBalanceSheets,
    kpi,
    setKpi,
    financialStatement,
    setFinancialStatement,
    filesParams,
    notesParams,
    selectedVersion,
    companyMeasurementDate,
    fieldAttributes,
    measurementDates,
    createdVersion,
    createVersion,
    fiscalYearData,
    setPeriods,
    setShowAdditionalHistoricalYears,
    additionalHistoricalYears,
    setAdditionalHistoricalYears,
    showAdditionalHistoricalYears,
    getVersions,
    currentFiscalYearEnd,
  } = useLoadFinancials({
    selectedMeasurementDate: selectedMeasurementDate as IMeasurementDate,
    filesToSave: filesToSave as File[],
    setFilesToSave: setFilesToSave as (files: File[]) => void,
    notes,
    saveNotes,
    notesHasChanged,
    spreadsheetNotes,
    spreadsheetNoteHasChanged,
    saveSpreadsheetNotes,
  });
  const [isDisplayingRowNumbers, setIsDisplayingRowNumbers] = useState(false);
  const [useAdjustedEBITDA, setUseAdjustedEBITDA] = useState<boolean | undefined>(undefined);
  const [isAllocationFinal, setIsAllocationFinal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [primaryCaptable, getPrimaryCaptable] = useGetPrimaryCaptableByCompanyDate() as unknown as [
    CompanyInfo,
    (companyId: number) => Promise<void>,
  ];
  const { setCurrentSelectedValues } = useContext(ExcelExportContext) as unknown as ExcelExportContextValues;

  useEffect(() => {
    setCurrentSelectedValues?.({
      financial_statement: financialStatement?.id,
    });
    if (financialStatement) {
      setUseAdjustedEBITDA(financialStatement?.use_adjusted_ebitda);
    }
  }, [financialStatement, setCurrentSelectedValues]);

  useEffect(() => {
    if (companyMeasurementDate?.id) {
      getPrimaryCaptable(companyMeasurementDate.id);
    }
  }, [companyMeasurementDate, getPrimaryCaptable]);

  useEffect(() => {
    if (!isEmpty(primaryCaptable)) {
      const verifyAllocationStatus = async () => {
        try {
          const captableService = new CaptableService();
          const finalAllocation = await captableService.gotFinalAllocation(primaryCaptable?.id as number);
          setIsAllocationFinal(!isEmpty(finalAllocation));
          setIsDisabled(!isEmpty(finalAllocation) || viewOnlyUser);
          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
        } catch (error) {
          setIsAllocationFinal(false);
          setIsDisabled(false);
          throw new Error(error as string);
        }
      };
      verifyAllocationStatus();
    }
  }, [primaryCaptable, viewOnlyUser, isExportMode]);

  const baseProps = useMemo(
    () => ({
      periods,
      setPeriods,
      changePeriodDate,
      measurementDate: selectedMeasurementDate as IMeasurementDate,
      fieldAttributes,
      financialStatement,
      setFinancialStatement,
      companyMeasurementDate,
      selectedVersion,
      measurementDates: measurementDates as IMeasurementDate[],
      createdVersion,
      createVersion,
      fiscalYearData,
      filesParams,
      notesParams,
      setShowAdditionalHistoricalYears,
      additionalHistoricalYears,
      setAdditionalHistoricalYears,
      showAdditionalHistoricalYears,
      getVersions,
      currentFiscalYearEnd,
      setIsDisplayingRowNumbers,
      useAdjustedEBITDA,
      isAllocationFinal,
      isDisabled,
      setIsDisabled,
      viewOnlyUser,
    }),
    [
      filesParams,
      notesParams,
      changePeriodDate,
      fieldAttributes,
      periods,
      setPeriods,
      selectedMeasurementDate,
      financialStatement,
      setFinancialStatement,
      companyMeasurementDate,
      measurementDates,
      selectedVersion,
      createdVersion,
      createVersion,
      fiscalYearData,
      setShowAdditionalHistoricalYears,
      additionalHistoricalYears,
      setAdditionalHistoricalYears,
      showAdditionalHistoricalYears,
      getVersions,
      currentFiscalYearEnd,
      setIsDisplayingRowNumbers,
      useAdjustedEBITDA,
      isAllocationFinal,
      isDisabled,
      setIsDisabled,
      viewOnlyUser,
    ]
  );

  const documentReferencesProps = useMemo(
    () => ({
      selectedMeasurementDate,
      currentPage: FINANCIALS_REFERENCE_TITLE,
      referenceType: FINANCIALS_REFERENCE_TYPE,
      referencedFeatureId: financialStatement?.id,
      filesToSave,
      setFilesToSave,
      isDisplayingRowNumbers,
      isDisabled,
    }),
    [filesToSave, financialStatement?.id, isDisabled, isDisplayingRowNumbers, selectedMeasurementDate, setFilesToSave]
  );

  const notesProps = useMemo(
    () => ({
      pageType: FINANCIALS_REFERENCE_TYPE,
      pageTypeKey: FINANCIALS_PAGE_KEY,
      pageTypeId: financialStatement?.id,
      notes,
      setNotes,
      onAddNote,
      onUpdateNotes,
      onDeleteNote,
      isDisabled,
    }),
    [financialStatement?.id, isDisabled, notes, onAddNote, onDeleteNote, onUpdateNotes, setNotes]
  );

  const spreadsheetNotesProps = useMemo(
    () => ({
      notes: spreadsheetNotes,
      setNotes: setSpreadsheetNotes,
      saveNotes: saveSpreadsheetNotes,
      handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
      pageType: FINANCIALS_REFERENCE_TYPE,
      pageTypeKey: FINANCIALS_PAGE_KEY,
      pageTypeId: financialStatement?.id,
    }),
    [financialStatement?.id, handleOnDeleteSpreadsheetNote, saveSpreadsheetNotes, setSpreadsheetNotes, spreadsheetNotes]
  );

  if (!periods) {
    return <GridSkeleton />;
  }
  return (
    <DocumentFootnotesProvider>
      <ActiveCellProvider>
        <Root>
          <MDDialog />
          <FinancialsProvider
            periods={periods}
            incomeStatement={incomeStatement}
            setIncomeStatement={setIncomeStatement}
            balanceSheets={balanceSheets}
            useAdjustedEBITDA={useAdjustedEBITDA}
            setUseAdjustedEBITDA={setUseAdjustedEBITDA}
            setBalanceSheets={setBalanceSheets}
            kpi={kpi}
            setKpi={setKpi}>
            <div id="financials">
              <div>
                <div>
                  <FinancialsTables
                    {...baseProps}
                    widgetDrawerProps={{
                      applicationContext: WidgetDrawerApplicationContext.FINANCIALS,
                      documentReferencesProps,
                      notesProps,
                      spreadsheetNotesProps,
                    }}
                  />
                </div>
              </div>
            </div>
          </FinancialsProvider>
        </Root>
      </ActiveCellProvider>
    </DocumentFootnotesProvider>
  );
};

export default Financial;
